import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import {
  AttendanceJustificationAttendanceEffect,
  AttendanceJustificationPayEffect,
  AttendanceJustificationScopeAllowed,
  AttendanceJustificationTypeFormValue,
  AttendanceJustificationTypePayload,
  AttendanceJustificationTypeRecord,
} from './attendance-justification-type.interface';

type AttendanceJustificationTypeMode = 'view' | 'create' | 'edit';

@Component({
  selector: 'app-attendance-justification-type-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    LoadingStateComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  templateUrl: './attendance-justification-type-detail.component.html',
  styleUrl: './attendance-justification-type-detail.component.scss',
})
export class AttendanceJustificationTypeDetailComponent implements OnInit {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: AttendanceJustificationTypeMode = 'view';
  selectedAttendanceJustificationTypeId: string | null = null;
  selectedAttendanceJustificationType: AttendanceJustificationTypeRecord | null = null;
  form: AttendanceJustificationTypeFormValue = this.createEmptyForm();

  readonly readAccess = { resource: 'attendanceJustificationTypes', action: 'read' } as const;
  readonly createAccess = { resource: 'attendanceJustificationTypes', action: 'create' } as const;
  readonly updateAccess = { resource: 'attendanceJustificationTypes', action: 'update' } as const;
  readonly deleteAccess = { resource: 'attendanceJustificationTypes', action: 'delete' } as const;

  readonly payEffectOptions: AttendanceJustificationPayEffect[] = ['paid', 'unpaid'];
  readonly attendanceEffectOptions: AttendanceJustificationAttendanceEffect[] = ['justify_day', 'justify_delay', 'justify_early_exit'];
  readonly toggleOptions: Array<keyof Pick<
    AttendanceJustificationTypeFormValue,
    'requiresApproval' | 'requireComment' | 'requireAttachment' | 'allowEmployeeCreate' | 'allowAdminCreate'
  >> = ['requiresApproval', 'requireComment', 'requireAttachment', 'allowEmployeeCreate', 'allowAdminCreate'];

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.selectedAttendanceJustificationType) {
        this.selectedAttendanceJustificationType = this.normalizeRecord(this.selectedAttendanceJustificationType);
      }
    });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.syncWithRoute();
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('attendanceJustificationTypesCatalog.detail.createTitle');
    }
    if (this.isEditMode) {
      return this.t('attendanceJustificationTypesCatalog.detail.editTitle');
    }
    return this.selectedAttendanceJustificationType?.label || this.t('attendanceJustificationTypesCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('attendanceJustificationTypesCatalog.detail.createDescription');
    }
    if (this.isEditMode) {
      return this.t('attendanceJustificationTypesCatalog.detail.editDescription');
    }
    return this.t('attendanceJustificationTypesCatalog.detail.description');
  }

  startCreate(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/attendance-justification-types/new']);
  }

  startEdit(): void {
    if (!this.selectedAttendanceJustificationTypeId || !this.hasAccess('update')) {
      return;
    }
    void this.router.navigate(['/attendance/attendance-justification-types', this.selectedAttendanceJustificationTypeId, 'edit']);
  }

  cancelEdit(): void {
    if (this.selectedAttendanceJustificationTypeId) {
      void this.router.navigate(['/attendance/attendance-justification-types', this.selectedAttendanceJustificationTypeId]);
      return;
    }
    void this.router.navigate(['/attendance/attendance-justification-types']);
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/attendance-justification-types']);
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const created = await firstValueFrom(
          this.apiService.post<AttendanceJustificationTypeRecord>('attendance-justification-types', this.buildPayload())
        );
        this.showNotification(
          'snackbar-success',
          this.t('attendanceJustificationTypesCatalog.notifications.created'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/attendance-justification-types', created.attendanceJustificationTypeId]);
      } else if (this.isEditMode && this.selectedAttendanceJustificationTypeId) {
        await firstValueFrom(
          this.apiService.patch<AttendanceJustificationTypeRecord>(
            `attendance-justification-types/${this.selectedAttendanceJustificationTypeId}`,
            this.buildPayload()
          )
        );
        this.showNotification(
          'snackbar-success',
          this.t('attendanceJustificationTypesCatalog.notifications.updated'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/attendance-justification-types', this.selectedAttendanceJustificationTypeId]);
      }
    } catch (error) {
      console.error('Error saving attendance justification type', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('attendanceJustificationTypesCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedAttendanceJustificationType || !this.selectedAttendanceJustificationTypeId || !this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('attendanceJustificationTypesCatalog.dialog.deleteTitle'),
        message: this.t('attendanceJustificationTypesCatalog.dialog.deleteMessage', {
          justificationType: this.selectedAttendanceJustificationType.label,
        }),
        confirmLabel: this.t('attendanceJustificationTypesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedAttendanceJustificationTypeId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(
          this.apiService.delete(`attendance-justification-types/${this.selectedAttendanceJustificationTypeId}`)
        );
        this.showNotification(
          'snackbar-success',
          this.t('attendanceJustificationTypesCatalog.notifications.deleted'),
          'bottom',
          'center'
        );
        void this.router.navigate(['/attendance/attendance-justification-types']);
      } catch (error) {
        console.error('Error deleting attendance justification type', error);
        this.showNotification(
          'snackbar-danger',
          this.getApiErrorMessage(error, this.t('attendanceJustificationTypesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  private async syncWithRoute(): Promise<void> {
    const attendanceJustificationTypeId = this.route.snapshot.paramMap.get('attendanceJustificationTypeId');
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/attendance-justification-types/new');
    const isEditRoute = /\/attendance\/attendance-justification-types\/[^/]+\/edit$/.test(currentUrl);

    if (isCreateRoute) {
      this.mode = 'create';
      this.selectedAttendanceJustificationTypeId = null;
      this.selectedAttendanceJustificationType = null;
      this.form = this.createEmptyForm();
      this.isLoading = false;
      return;
    }

    if (!attendanceJustificationTypeId) {
      this.mode = 'view';
      this.selectedAttendanceJustificationTypeId = null;
      this.selectedAttendanceJustificationType = null;
      this.isLoading = false;
      return;
    }

    await this.loadAttendanceJustificationType(attendanceJustificationTypeId);
    this.mode = isEditRoute ? 'edit' : 'view';
  }

  private async loadAttendanceJustificationType(attendanceJustificationTypeId: string): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<AttendanceJustificationTypeRecord>(
          `attendance-justification-types/${attendanceJustificationTypeId}`
        )
      );
      const normalized = this.normalizeRecord(response);
      this.selectedAttendanceJustificationTypeId = normalized.attendanceJustificationTypeId;
      this.selectedAttendanceJustificationType = normalized;
      this.form = this.createFormFromRecord(normalized);
    } catch (error) {
      console.error('Error loading attendance justification type detail', error);
      this.showNotification(
        'snackbar-danger',
        this.getApiErrorMessage(error, this.t('attendanceJustificationTypesCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private createEmptyForm(): AttendanceJustificationTypeFormValue {
    return {
      code: '',
      label: '',
      description: '',
      scopeAllowed: 'full_day',
      requiresApproval: true,
      payEffect: 'paid',
      attendanceEffect: 'justify_day',
      requireComment: true,
      requireAttachment: false,
      allowEmployeeCreate: true,
      allowAdminCreate: true,
      isActive: true,
    };
  }

  private createFormFromRecord(item: AttendanceJustificationTypeRecord): AttendanceJustificationTypeFormValue {
    return {
      code: item.code,
      label: item.label,
      description: item.description || '',
      scopeAllowed: item.scopeAllowed,
      requiresApproval: item.requiresApproval,
      payEffect: item.payEffect,
      attendanceEffect: item.attendanceEffect,
      requireComment: item.requireComment,
      requireAttachment: item.requireAttachment,
      allowEmployeeCreate: item.allowEmployeeCreate,
      allowAdminCreate: item.allowAdminCreate,
      isActive: item.isActive !== false,
    };
  }

  private buildPayload(): AttendanceJustificationTypePayload {
    return {
      code: this.normalizeText(this.form.code),
      label: this.normalizeText(this.form.label),
      description: this.normalizeOptionalText(this.form.description) || null,
      // El alcance ya no se captura en la interfaz. Se deriva del efecto para
      // conservar compatibilidad con versiones del API que aún reciben el campo.
      scopeAllowed: this.form.attendanceEffect === 'justify_day' ? 'full_day' : 'partial_minutes',
      requiresApproval: this.form.requiresApproval,
      payEffect: this.form.payEffect,
      attendanceEffect: this.form.attendanceEffect,
      requireComment: this.form.requireComment,
      requireAttachment: this.form.requireAttachment,
      allowEmployeeCreate: this.form.allowEmployeeCreate,
      allowAdminCreate: this.form.allowAdminCreate,
      isActive: this.form.isActive,
    };
  }

  private validateForm(): string | null {
    if (!this.normalizeText(this.form.code)) {
      return this.t('attendanceJustificationTypesCatalog.notifications.validationCode');
    }
    if (!this.normalizeText(this.form.label)) {
      return this.t('attendanceJustificationTypesCatalog.notifications.validationLabel');
    }
    if (!this.normalizeText(this.form.payEffect)) {
      return this.t('attendanceJustificationTypesCatalog.notifications.validationPayEffect');
    }
    if (!this.normalizeText(this.form.attendanceEffect)) {
      return this.t('attendanceJustificationTypesCatalog.notifications.validationAttendanceEffect');
    }
    return null;
  }

  private normalizeRecord(item: Partial<AttendanceJustificationTypeRecord>): AttendanceJustificationTypeRecord {
    const scopeAllowed = this.normalizeText(item.scopeAllowed) as AttendanceJustificationScopeAllowed || 'full_day';
    const payEffect = this.normalizeText(item.payEffect) as AttendanceJustificationPayEffect || 'paid';
    const attendanceEffect = this.normalizeText(item.attendanceEffect) as AttendanceJustificationAttendanceEffect || 'justify_day';
    const isActive = item.isActive !== false;

    return {
      attendanceJustificationTypeId: item.attendanceJustificationTypeId || '',
      center: item.center || '',
      code: item.code || '',
      label: item.label || '',
      description: item.description || '',
      scopeAllowed,
      requiresApproval: item.requiresApproval === true,
      payEffect,
      attendanceEffect,
      requireComment: item.requireComment === true,
      requireAttachment: item.requireAttachment === true,
      allowEmployeeCreate: item.allowEmployeeCreate === true,
      allowAdminCreate: item.allowAdminCreate === true,
      isActive,
      createdAt: item.createdAt || null,
      updatedAt: item.updatedAt || null,
      scopeAllowedLabel: this.t(`attendanceJustificationTypesCatalog.scopeAllowedOptions.${scopeAllowed}`),
      payEffectLabel: this.t(`attendanceJustificationTypesCatalog.payEffectOptions.${payEffect}`),
      attendanceEffectLabel: this.t(`attendanceJustificationTypesCatalog.attendanceEffectOptions.${attendanceEffect}`),
      requiresApprovalLabel: item.requiresApproval === true ? this.t('common.yes') : this.t('common.no'),
      requireCommentLabel: item.requireComment === true ? this.t('common.yes') : this.t('common.no'),
      requireAttachmentLabel: item.requireAttachment === true ? this.t('common.yes') : this.t('common.no'),
      allowEmployeeCreateLabel: item.allowEmployeeCreate === true ? this.t('common.yes') : this.t('common.no'),
      allowAdminCreateLabel: item.allowAdminCreate === true ? this.t('common.yes') : this.t('common.no'),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
      createdAtLabel: this.formatDateTime(item.createdAt),
      updatedAtLabel: this.formatDateTime(item.updatedAt),
    };
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private normalizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private normalizeOptionalText(value: unknown): string | undefined {
    const normalized = this.normalizeText(value);
    return normalized || undefined;
  }

  private getApiErrorMessage(error: unknown, fallback: string): string {
    return extractApiErrorMessage(error, fallback);
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'attendanceJustificationTypes', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
